import { LineItemType, type CartLineT } from '@lambda/apis/src/cart/types';
import { isInternalUser } from '@/lib/internal-user';
import { ORDER_DISCOUNT_BADGE_FLOOR, ORDER_DISCOUNT_TIERS } from './config';
import type {
  OrderDiscountStateT,
  OrderDiscountTierStateT,
  OrderDiscountTierT,
} from './types';

// Nothing to show: no badge, no summary breakdown, total untouched.
export const NO_ORDER_DISCOUNT: OrderDiscountStateT = {
  visible: false,
  tiers: [],
  activeTier: null,
  nextTier: null,
  remaining: 0,
  discount: 0,
};

// Whether threshold discounts are turned on for this shopper. Signed-out
// shoppers are outside the internal rollout, so they see nothing.
export const isOrderDiscountEnabled = (email?: string | null): boolean =>
  isInternalUser(email);

/**
 * What a threshold is measured against: everything the shopper will be charged.
 *
 * Every line type counts — devices, accessories, ReebeloCare and express
 * shipping alike — so an add-on both helps reach a tier and is discounted by
 * it. Buyer protection needs no handling of its own: it is a component of a
 * product line's own price rather than a line of its own.
 */
export const getCartDiscountSubtotal = (
  cartLines: CartLineT[],
  reebeloCareSubtotal = 0,
): number =>
  cartLines
    .filter((line) => line.type !== LineItemType.WARRANTY)
    .reduce((total, line) => total + Number(line.price) * line.quantity, 0) +
  reebeloCareSubtotal;

/**
 * The tiers worth listing in the badge tooltip.
 *
 * Tiers do not stack — only `activeTier` comes off the subtotal — but every
 * tier the subtotal has passed is flagged `applied`, so a $5,000 cart listed
 * "-3% applied" and "-5% applied" together and read as 8% off. A tier that has
 * been superseded is dropped, leaving the one actually in effect plus the ones
 * still to aim for.
 */
export const getDisplayedTiers = ({
  tiers,
  activeTier,
}: Pick<
  OrderDiscountStateT,
  'tiers' | 'activeTier'
>): OrderDiscountTierStateT[] =>
  tiers.filter(
    (tier) => !tier.applied || tier.threshold === activeTier?.threshold,
  );

/**
 * Resolves which threshold a cart subtotal has earned, how far the next one is,
 * and whether the badge should show at all. All amounts are in minor units
 * (cents), matching the cart payload.
 */
export const getOrderDiscountState = (
  subtotal: number,
  tiers: OrderDiscountTierT[] = ORDER_DISCOUNT_TIERS,
  badgeFloor: number = ORDER_DISCOUNT_BADGE_FLOOR,
): OrderDiscountStateT => {
  const tierStates: OrderDiscountTierStateT[] = tiers.map((tier) => ({
    ...tier,
    applied: subtotal >= tier.threshold,
  }));

  // Tiers are sorted by threshold, so the last applied one is the best one.
  const activeTier =
    [...tierStates].reverse().find((tier) => tier.applied) ?? null;
  const nextTier = tierStates.find((tier) => !tier.applied) ?? null;

  return {
    visible: tierStates.length > 0 && subtotal >= badgeFloor,
    tiers: tierStates,
    activeTier,
    nextTier,
    remaining: nextTier ? nextTier.threshold - subtotal : 0,
    discount: activeTier
      ? Math.round((subtotal * activeTier.percentage) / 100)
      : 0,
  };
};
